import React, { useState, useEffect } from 'react';
import { getApiUrl } from '../../utils/apiUrl';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { 
  FiUsers, FiPlus, FiTrash2, FiEdit2, FiKey, FiCheck, FiX,
  FiMail, FiPhone, FiCalendar, FiAlertCircle, FiUserCheck, FiUserX
} from 'react-icons/fi';
import { toast } from 'sonner';

const API_URL = getApiUrl();

const CollaboratorsPage = () => {
  const [collaborators, setCollaborators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [selectedCollab, setSelectedCollab] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    permissions: ['view_assigned_reservations']
  });

  useEffect(() => {
    loadCollaborators();
  }, []);

  const loadCollaborators = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/collaborators`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCollaborators(data);
      }
    } catch (error) {
      toast.error('Erreur lors du chargement des collaborateurs');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/collaborators`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        toast.success('Collaborateur créé avec succès');
        setShowCreateModal(false);
        setFormData({ name: '', email: '', phone: '', password: '', permissions: ['view_assigned_reservations'] });
        loadCollaborators();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Erreur lors de la création');
      }
    } catch (error) {
      toast.error('Erreur lors de la création');
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!selectedCollab) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/collaborators/${selectedCollab.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          is_active: formData.is_active,
          permissions: formData.permissions
        })
      });
      
      if (response.ok) {
        toast.success('Collaborateur mis à jour');
        setShowEditModal(false);
        loadCollaborators();
      } else {
        toast.error('Erreur lors de la mise à jour');
      }
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!selectedCollab) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/collaborators/${selectedCollab.id}/reset-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ password: formData.password })
      });
      
      if (response.ok) {
        toast.success('Mot de passe réinitialisé');
        setShowResetPasswordModal(false);
        setFormData({ ...formData, password: '' });
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Erreur');
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleDelete = async (collabId) => {
    if (!window.confirm('Supprimer ce collaborateur ? Il sera retiré de toutes les réservations.')) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/collaborators/${collabId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Collaborateur supprimé');
        loadCollaborators();
      } else {
        toast.error('Erreur lors de la suppression');
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const toggleActive = async (collab) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/collaborators/${collab.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_active: !collab.is_active })
      });
      
      if (response.ok) {
        toast.success(collab.is_active ? 'Collaborateur désactivé' : 'Collaborateur activé');
        loadCollaborators();
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const openEditModal = (collab) => {
    setSelectedCollab(collab);
    setFormData({
      name: collab.name,
      email: collab.email,
      phone: collab.phone || '',
      is_active: collab.is_active,
      permissions: collab.permissions || ['view_assigned_reservations']
    });
    setShowEditModal(true);
  };

  const openResetPasswordModal = (collab) => {
    setSelectedCollab(collab);
    setFormData({ ...formData, password: '' });
    setShowResetPasswordModal(true);
  };

  return (
    <div className="space-y-6" data-testid="collaborators-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <FiUsers className="text-indigo-500" />
            Collaborateurs
          </h1>
          <p className="text-slate-500 mt-1">
            Gérez les accès de votre équipe aux réservations
          </p>
        </div>
        <Button
          onClick={() => {
            setFormData({ name: '', email: '', phone: '', password: '', permissions: ['view_assigned_reservations'] });
            setShowCreateModal(true);
          }}
          className="bg-indigo-600 hover:bg-indigo-700"
          data-testid="add-collaborator-btn"
        >
          <FiPlus className="mr-2" /> Ajouter un collaborateur
        </Button>
      </div>

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-100">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <FiAlertCircle className="text-blue-500 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium">À propos des collaborateurs</p>
              <p className="text-blue-600 mt-1">
                Les collaborateurs peuvent se connecter et ne voient que les réservations qui leur sont assignées.
                Ils n'ont pas accès aux paramètres, à la facturation ou aux autres fonctionnalités admin.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Collaborators List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>
      ) : collaborators.length === 0 ? (
        <Card className="py-12 text-center">
          <FiUsers className="mx-auto h-12 w-12 text-slate-300" />
          <h3 className="mt-4 text-lg font-medium text-slate-700">Aucun collaborateur</h3>
          <p className="text-slate-500 mt-2">Créez votre premier collaborateur pour lui donner accès aux réservations.</p>
          <Button
            onClick={() => setShowCreateModal(true)}
            className="mt-4 bg-indigo-600 hover:bg-indigo-700"
          >
            <FiPlus className="mr-2" /> Créer un collaborateur
          </Button>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          <AnimatePresence>
            {collaborators.map((collab, index) => (
              <motion.div
                key={collab.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card className={`relative overflow-hidden ${!collab.is_active ? 'opacity-60 bg-slate-50' : ''}`}>
                  {/* Status Badge */}
                  <div className={`absolute top-0 right-0 px-3 py-1 text-xs font-medium rounded-bl-lg ${
                    collab.is_active 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-slate-200 text-slate-600'
                  }`}>
                    {collab.is_active ? 'Actif' : 'Désactivé'}
                  </div>
                  
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-semibold">
                        {collab.name?.charAt(0).toUpperCase() || '?'}
                      </div>
                      <span>{collab.name}</span>
                    </CardTitle>
                  </CardHeader>
                  
                  <CardContent className="space-y-3">
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <FiMail className="text-slate-400" />
                      <span>{collab.email}</span>
                    </div>
                    
                    {collab.phone && (
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <FiPhone className="text-slate-400" />
                        <span>{collab.phone}</span>
                      </div>
                    )}
                    
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <FiCalendar className="text-slate-400" />
                      <span>
                        {collab.last_login 
                          ? `Dernière connexion: ${new Date(collab.last_login).toLocaleDateString('fr-FR')}`
                          : 'Jamais connecté'}
                      </span>
                    </div>
                    
                    <div className="bg-slate-50 px-3 py-2 rounded-lg">
                      <p className="text-xs text-slate-500">Réservations assignées</p>
                      <p className="text-lg font-bold text-indigo-600">{collab.assigned_count || 0}</p>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-center gap-2 pt-2 border-t">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleActive(collab)}
                        className="flex-1"
                      >
                        {collab.is_active ? (
                          <><FiUserX className="mr-1" /> Désactiver</>
                        ) : (
                          <><FiUserCheck className="mr-1" /> Activer</>
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditModal(collab)}
                      >
                        <FiEdit2 />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openResetPasswordModal(collab)}
                      >
                        <FiKey />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(collab.id)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <FiTrash2 />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}

      {/* Create Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nouveau Collaborateur</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div>
              <Label>Nom complet *</Label>
              <Input
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Jean Dupont"
              />
            </div>
            <div>
              <Label>Email *</Label>
              <Input
                required
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="jean@example.com"
              />
            </div>
            <div>
              <Label>Téléphone</Label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="06 12 34 56 78"
              />
            </div>
            <div>
              <Label>Mot de passe *</Label>
              <Input
                required
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="Minimum 6 caractères"
                minLength={6}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setShowCreateModal(false)}>
                Annuler
              </Button>
              <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700">
                <FiCheck className="mr-2" /> Créer
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifier le collaborateur</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdate} className="space-y-4">
            <div>
              <Label>Nom complet *</Label>
              <Input
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                disabled
                value={formData.email}
                className="bg-slate-100"
              />
              <p className="text-xs text-slate-500 mt-1">L'email ne peut pas être modifié</p>
            </div>
            <div>
              <Label>Téléphone</Label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setShowEditModal(false)}>
                Annuler
              </Button>
              <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700">
                <FiCheck className="mr-2" /> Enregistrer
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reset Password Modal */}
      <Dialog open={showResetPasswordModal} onOpenChange={setShowResetPasswordModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Réinitialiser le mot de passe</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleResetPassword} className="space-y-4">
            <p className="text-sm text-slate-600">
              Définissez un nouveau mot de passe pour <strong>{selectedCollab?.name}</strong>
            </p>
            <div>
              <Label>Nouveau mot de passe *</Label>
              <Input
                required
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="Minimum 6 caractères"
                minLength={6}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setShowResetPasswordModal(false)}>
                Annuler
              </Button>
              <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700">
                <FiKey className="mr-2" /> Réinitialiser
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CollaboratorsPage;
